import type { TypedSupabaseClient } from '../client';
import type {
  ICaisseRepository,
  CaisseSettings,
  CashSession,
  CashSessionStats,
  CashMovement,
  CashSessionFilters,
  OpenCashSessionInput,
  CloseCashSessionInput,
  RecordCashCountInput,
  CreateCashMovementInput,
  UpdateCaisseSettingsInput,
} from '../../../interfaces/src';
import { ConfirmedMutationRejectedError } from '../../../interfaces/src';
import { buildDocumentNumber } from '@erp/domain/numbering';
import {
  currentBusinessDay,
  DEFAULT_CAISSE_SETTINGS,
  resolveBusinessDay,
  resolveCountedAmount,
  sanitizeBreakdown,
  type CashCountBreakdown,
} from '@erp/domain/caisse';
import { resolveDocNumberYear } from './doc-number-year';
import { chunkIds, fetchAllRows } from './fetch-all-rows';

/**
 * Erreur de conflit de session, préservant le code Postgres.
 *
 * Le moteur de synchronisation classe une erreur d'après son `code` et son
 * message (libs/native/interfaces/src/sync-error.ts). L'ancienne version
 * aplatissait le 23505 en `new Error('caisse.errors.alreadyOpen')` — un Error
 * nu, sans code, que le classificateur rangeait en `unknown` : 8 tentatives
 * puis mise au rebut définitive. Avec l'ouverture AUTOMATIQUE, deux postes qui
 * démarrent le même matin déclenchent ce conflit tous les jours, donc on
 * conserve le code pour que la classification reste juste.
 */
class CashSessionConflictError extends Error {
  readonly code = '23505';
  constructor(
    message: string,
    /** Session gagnante, quand on a pu la relire. */
    readonly existingSession: CashSession | null,
  ) {
    super(message);
    this.name = 'CashSessionConflictError';
  }
}

const SESSION_COLUMNS = '*';

export class SupabaseCaisseRepository implements ICaisseRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  // ------------------------------------------------------------------
  // Lecture
  // ------------------------------------------------------------------

  /**
   * PostgREST plafonne silencieusement à 1000 lignes. Avec une session
   * automatique par caisse et par jour, un commerce à deux caisses franchit ce
   * plafond en ~16 mois — et une comparaison année sur année perdrait alors
   * les plus anciennes données SANS la moindre erreur. On pagine.
   */
  async listSessions(filters?: CashSessionFilters): Promise<CashSession[]> {
    const rows = await fetchAllRows<Record<string, unknown>>(async (from, to) => {
      let query = this.supabase.from('cash_sessions').select(SESSION_COLUMNS);

      if (filters?.bankAccountId) query = query.eq('bank_account_id', filters.bankAccountId);
      if (filters?.isClosed !== undefined) query = query.eq('is_closed', filters.isClosed);
      if (filters?.dateFrom) query = query.gte('opened_at', filters.dateFrom);
      if (filters?.dateTo) query = query.lte('opened_at', filters.dateTo);
      if (filters?.fromBusinessDay) query = query.gte('session_date', filters.fromBusinessDay);
      if (filters?.toBusinessDay) query = query.lte('session_date', filters.toBusinessDay);

      // Ordre STABLE et total : `session_date` seul n'est pas unique (plusieurs
      // caisses, plusieurs relèves), deux pages pourraient se chevaucher.
      return query
        .order('session_date', { ascending: false })
        .order('opened_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);
    });
    return rows.map(mapSession);
  }

  async getSessionById(id: string): Promise<CashSession | null> {
    const { data, error } = await this.supabase
      .from('cash_sessions')
      .select(SESSION_COLUMNS)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapSession(data) : null;
  }

  async getOpenSession(bankAccountId: string): Promise<CashSession | null> {
    const { data, error } = await this.supabase
      .from('cash_sessions')
      .select(SESSION_COLUMNS)
      .eq('bank_account_id', bankAccountId)
      .eq('is_closed', false)
      .maybeSingle();
    if (error) throw error;
    return data ? mapSession(data) : null;
  }

  async listOpenSessions(): Promise<CashSession[]> {
    const { data, error } = await this.supabase
      .from('cash_sessions')
      .select(SESSION_COLUMNS)
      .eq('is_closed', false)
      .order('opened_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapSession);
  }

  async getLastClosedSession(bankAccountId: string): Promise<CashSession | null> {
    const { data, error } = await this.supabase
      .from('cash_sessions')
      .select(SESSION_COLUMNS)
      .eq('bank_account_id', bankAccountId)
      .eq('is_closed', true)
      .order('session_date', { ascending: false })
      .order('closed_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ? mapSession(data) : null;
  }

  // ------------------------------------------------------------------
  // Ouverture
  // ------------------------------------------------------------------

  async openSession(input: OpenCashSessionInput): Promise<CashSession> {
    // La journée commerciale est TOUJOURS résolue ici quand l'appelant ne l'a
    // pas fournie. Sans ça, l'insertion retombait sur le DEFAULT SQL
    // `(now() AT TIME ZONE 'Africa/Tunis')::date`, qui ignore
    // `day_cutoff_minutes` : un café qui bascule à 04 h et ouvre son tiroir à
    // 01 h 30 se retrouvait rattaché à la MAUVAISE journée — donc à côté de sa
    // propre session automatique, hors de l'index d'idempotence, et avec des
    // statistiques réparties sur deux journées.
    // Fond compté coupure par coupure : c'est le DÉTAIL qui fait foi, le montant
    // est recalculé depuis lui (`resolveCountedAmount`, domaine). Sans cet
    // arbitrage, un fond « 200,000 DT » pourrait cohabiter avec un détail qui
    // somme à 198,500 et le report du lendemain partirait du mauvais nombre.
    const openingCount = resolveCountedAmount(input.openingBalance, input.openingBreakdown);
    const resolved: OpenCashSessionInput = {
      ...input,
      openingBalance: openingCount.amount ?? 0,
      openingBreakdown: openingCount.breakdown,
      sessionDate: input.sessionDate ?? (await this.resolveSessionDate(input.openedAt)),
    };
    const number = await this.getNextSessionNumber();

    const payload: Record<string, unknown> = {
      number,
      bank_account_id: resolved.bankAccountId,
      opened_by: resolved.openedBy ?? null,
      opening_balance: resolved.openingBalance,
      opening_breakdown: openingCount.breakdown,
      notes: resolved.notes ?? null,
      open_mode: resolved.openMode ?? 'manual',
      session_date: resolved.sessionDate,
    };
    if (resolved.openedAt) payload['opened_at'] = resolved.openedAt;

    const { data: session, error } = await this.supabase
      .from('cash_sessions')
      .insert(payload as never)
      .select(SESSION_COLUMNS)
      .single();

    if (error) {
      if (isUniqueViolation(error)) {
        const existing = await this.findConflictingSession(resolved);
        throw new CashSessionConflictError('caisse.errors.alreadyOpen', existing);
      }
      throw error;
    }

    await this.insertMovement({
      sessionId: (session as { id: string }).id,
      movementType: 'opening',
      amount: resolved.openingBalance,
      description: 'Ouverture de caisse',
      breakdown: openingCount.breakdown,
      createdBy: resolved.openedBy ?? null,
      createdAt: resolved.openedAt,
    });

    return mapSession(session);
  }

  /**
   * Journée commerciale d'un instant d'ouverture, d'après le décalage du
   * tenant. Une seule lecture des réglages ; des réglages illisibles ne doivent
   * jamais empêcher d'ouvrir une caisse, on retombe alors sur les défauts du
   * domaine (qui sont ceux des DEFAULT SQL).
   */
  private async resolveSessionDate(openedAt?: string): Promise<string> {
    let cutoff = DEFAULT_CAISSE_SETTINGS.dayCutoffMinutes;
    try {
      cutoff = (await this.getSettings()).dayCutoffMinutes;
    } catch {
      // Réglages inaccessibles : défaut du domaine.
    }
    const day = resolveBusinessDay(openedAt ? new Date(openedAt) : new Date(), cutoff);
    // `resolveBusinessDay` rend '' sur un instant illisible (openedAt corrompu).
    return day || currentBusinessDay(cutoff);
  }

  /**
   * Point d'entrée de l'automatisation : ouvre la session du jour, ou ADOPTE
   * celle qu'un autre poste vient de créer.
   *
   * L'index unique partiel `uq_cash_sessions_auto_day` garantit qu'un seul
   * poste gagne la course. Le perdant ne doit pas échouer — il doit repartir
   * avec la session gagnante, sinon ses encaissements se retrouveraient
   * orphelins ou dupliqueraient une deuxième journée.
   */
  async ensureAutoSession(input: OpenCashSessionInput): Promise<CashSession> {
    const resolved: OpenCashSessionInput = {
      ...input,
      sessionDate: input.sessionDate ?? (await this.resolveSessionDate(input.openedAt)),
    };

    // Chemin rapide : la session du jour existe déjà (cas ultra-majoritaire).
    const existing = await this.findAutoSessionForDay(
      resolved.bankAccountId,
      resolved.sessionDate,
    );
    // ⚠ `existing` peut être CLÔTURÉE. La renvoyer telle quelle briquait le
    // tiroir : toute écriture partait ensuite en `caisse.errors.sessionClosed`
    // jusqu'au lendemain (cf. `openSecondShift`).
    if (existing) {
      if (!existing.isClosed) return existing;
      return this.openSecondShift(resolved);
    }

    try {
      return await this.openSession({ ...resolved, openMode: 'auto' });
    } catch (err) {
      if (!(err instanceof CashSessionConflictError)) throw err;
      const winner =
        err.existingSession ?? (await this.findConflictingSession(resolved));
      if (winner && !winner.isClosed) return winner;
      // Le gagnant de la course a déjà été clôturé (relève éclair). Même
      // remède que ci-dessus — jamais renvoyer une session fermée.
      if (winner) return this.openSecondShift(resolved);
      throw err;
    }
  }

  /**
   * Rouvre une journée dont la session AUTOMATIQUE a été clôturée à la main.
   *
   * Cas réel : le caissier clôture le tiroir à 18 h, le magasin vend encore à
   * 19 h. La journée doit pouvoir encaisser à nouveau.
   *
   * Choix retenu — une SECONDE SESSION sur la même journée commerciale, en
   * `open_mode='manual'` :
   *
   *  - on ne rouvre JAMAIS la session clôturée (sa clôture est signée : montant
   *    compté, écart, instantané statistique ; la réécrire détruirait la
   *    preuve du comptage) ;
   *  - `'manual'` la place HORS de l'index partiel `uq_cash_sessions_auto_day`
   *    (qui ne couvre que `open_mode='auto'`) : la journée peut donc
   *    légitimement porter une deuxième relève sans violer la contrainte ;
   *  - `uq_cash_sessions_one_open_per_drawer` reste respecté parce qu'on adopte
   *    toute session déjà ouverte au lieu d'en créer une seconde.
   *
   * Aucune récursion : ce chemin ne rappelle pas `ensureAutoSession`, il ne
   * peut donc pas boucler. Au pire il adopte la session gagnante d'une course.
   *
   * Conséquence connue : `planSessionRollover` ne clôture jamais d'office une
   * session ouverte À LA MAIN. Cette relève de rattrapage restera donc ouverte
   * au passage de minuit tant que personne ne la clôture — la caisse fonctionne,
   * mais la journée suivante s'y rattache. C'est le prix d'un tiroir utilisable
   * plutôt que bloqué, et le caissier qui a clôturé à la main sait clôturer.
   */
  private async openSecondShift(input: OpenCashSessionInput): Promise<CashSession> {
    const stillOpen = await this.getOpenSession(input.bankAccountId);
    if (stillOpen) return stillOpen;

    try {
      return await this.openSession({ ...input, openMode: 'manual' });
    } catch (err) {
      if (err instanceof CashSessionConflictError) {
        const winner = await this.getOpenSession(input.bankAccountId);
        if (winner) return winner;
      }
      throw err;
    }
  }

  /**
   * Session automatique posée sur cette caisse pour cette journée, CLÔTURÉE OU
   * NON. L'état est volontairement laissé à l'appelant : c'est précisément la
   * présence d'une journée automatique déjà clôturée qui déclenche la seconde
   * relève.
   */
  private async findAutoSessionForDay(
    bankAccountId: string,
    sessionDate?: string,
  ): Promise<CashSession | null> {
    if (!sessionDate) return null;
    const { data, error } = await this.supabase
      .from('cash_sessions')
      .select(SESSION_COLUMNS)
      .eq('bank_account_id', bankAccountId)
      .eq('session_date', sessionDate)
      .eq('open_mode', 'auto')
      .maybeSingle();
    if (error) throw error;
    return data ? mapSession(data) : null;
  }

  /**
   * Relit la session qui a fait échouer l'insertion. Deux contraintes peuvent
   * se déclencher : « une seule session ouverte par tiroir » et « une seule
   * session automatique par tiroir et par jour ».
   *
   * On cherche la session OUVERTE en premier : c'est la seule que l'appelant
   * peut réellement utiliser. Commencer par la journée automatique renverrait
   * une session déjà clôturée alors qu'une autre est ouverte à côté.
   */
  private async findConflictingSession(
    input: OpenCashSessionInput,
  ): Promise<CashSession | null> {
    const open = await this.getOpenSession(input.bankAccountId);
    if (open) return open;
    return this.findAutoSessionForDay(input.bankAccountId, input.sessionDate);
  }

  // ------------------------------------------------------------------
  // Clôture / comptage
  // ------------------------------------------------------------------

  async closeSession(input: CloseCashSessionInput): Promise<CashSession> {
    const closedAt = input.closedAt ?? new Date().toISOString();
    // Le détail par coupures fait foi sur le montant (`resolveCountedAmount`,
    // domaine). Un tiroir NON compté garde `amount = null` : la colonne générée
    // `difference` reste alors NULL, et la clôture automatique ne fabrique
    // toujours pas d'écart de 0.
    const count = resolveCountedAmount(input.countedBalance, input.countedBreakdown);
    const updatePayload: Record<string, unknown> = {
      is_closed: true,
      closed_by: input.closedBy ?? null,
      closed_at: closedAt,
      expected_balance: input.expectedBalance,
      counted_balance: count.amount,
      counted_breakdown: count.breakdown,
      close_mode: input.closeMode ?? 'manual',
    };
    if (count.amount != null) {
      updatePayload['counted_at'] = closedAt;
      updatePayload['counted_by'] = input.closedBy ?? null;
    }
    if (input.notes !== undefined) updatePayload['notes'] = input.notes;
    if (input.stats) Object.assign(updatePayload, statsToColumns(input.stats));

    const { data: session, error } = await this.supabase
      .from('cash_sessions')
      .update(updatePayload as never)
      .eq('id', input.sessionId)
      .eq('is_closed', false)
      .select(SESSION_COLUMNS)
      .maybeSingle();
    if (error) throw error;
    if (!session) throw new Error('caisse.errors.sessionAlreadyClosed');

    const difference = (session as { difference: number | null }).difference;
    await this.insertMovement({
      sessionId: (session as { id: string }).id,
      movementType: 'closing',
      amount: count.amount ?? input.expectedBalance,
      description:
        difference == null
          ? 'Clôture — tiroir non compté'
          : `Clôture — écart ${difference}`,
      breakdown: count.breakdown,
      createdBy: input.closedBy ?? null,
      createdAt: closedAt,
    });

    return mapSession(session);
  }

  /**
   * Enregistre un comptage physique. Fonctionne aussi sur une session DÉJÀ
   * clôturée : c'est le « confirmez le fond de caisse » du lendemain matin, qui
   * inscrit rétroactivement l'écart de la veille.
   */
  async recordCount(input: RecordCashCountInput): Promise<CashSession> {
    const now = new Date().toISOString();
    // Lu AVANT toute écriture : sert à la fois à savoir si la session est déjà
    // clôturée (comptage rétroactif) et avec quel montant la session suivante
    // a été ouverte.
    const before = await this.getSessionById(input.sessionId);

    // LE montant du comptage, arbitré une seule fois pour tout ce qui suit : le
    // mouvement de constat, la régularisation, le report sur la journée suivante
    // et la colonne. Un détail par coupures l'emporte sur le montant saisi
    // (`resolveCountedAmount`, domaine) — sinon la régularisation pourrait
    // corriger le tiroir d'un delta calculé sur un montant que le détail
    // enregistré à côté contredit.
    const count = resolveCountedAmount(input.countedBalance, input.countedBreakdown);
    const countedBalance = count.amount ?? input.countedBalance;
    const adjustmentDelta = input.createAdjustment
      ? round3(countedBalance - input.expectedBalance)
      : 0;
    let followUpSessionId: string | null = null;
    let followUpDelta: number | null = null;
    if (before?.isClosed) {
      const carriedReference =
        before.countedBalance ?? before.expectedBalance ?? input.expectedBalance;
      const delta = round3(countedBalance - carriedReference);
      if (delta !== 0) {
        const followUp = await this.getOpenSession(before.bankAccountId);
        if (followUp && followUp.id !== before.id) {
          followUpSessionId = followUp.id;
          followUpDelta = delta;
        }
      }
    }

    const requestId = input.clientRequestId ?? globalThis.crypto.randomUUID();
    const rpcArgs = {
      p_client_request_id: requestId,
      p_session_id: input.sessionId,
      p_counted_balance: countedBalance,
      p_expected_balance: input.expectedBalance,
      p_counted_breakdown: readBreakdown(count.breakdown),
      p_adjustment_delta: adjustmentDelta === 0 ? null : adjustmentDelta,
      p_follow_up_session_id: followUpSessionId,
      p_follow_up_delta: followUpDelta,
      p_notes: input.notes ?? null,
      p_counted_at: now,
    };
    let { data: sessionId, error } = await this.supabase.rpc(
      'record_cash_count_atomic',
      rpcArgs,
    );
    if (
      (error && isAmbiguousCashMutationFailure(error))
      || (!error && !sessionId)
    ) {
      ({ data: sessionId, error } = await this.supabase.rpc(
        'record_cash_count_atomic',
        rpcArgs,
      ));
    }
    if (error || !sessionId) {
      if (error?.code === '23505') throw error;
      const isConfirmedDatabaseRejection = !!error?.code
        && /^[0-9A-Z]{5}$/.test(error.code);
      if (isConfirmedDatabaseRejection || !error) {
        const resolvedId = await this.resolveOrCancelAtomicRequest(
          requestId,
          'cash_count',
        );
        if (resolvedId) sessionId = resolvedId;
        else {
          throw new ConfirmedMutationRejectedError(
            error ?? new Error('atomic_cash_count_returned_no_id'),
          );
        }
      } else {
        throw error;
      }
    }
    const updated = await this.getSessionById(sessionId);
    if (!updated) throw new Error('atomic_cash_count_unavailable');
    return updated;
  }

  async saveSessionStats(sessionId: string, stats: CashSessionStats): Promise<void> {
    const { error } = await this.supabase
      .from('cash_sessions')
      .update(statsToColumns(stats) as never)
      .eq('id', sessionId);
    if (error) throw error;
  }

  async cancelEmptySession(id: string): Promise<void> {
    const { error } = await this.supabase.rpc(
      'cancel_empty_cash_session_atomic',
      { p_session_id: id },
    );
    if (error) throw error;
  }

  // ------------------------------------------------------------------
  // Mouvements
  // ------------------------------------------------------------------

  async listMovements(sessionId: string): Promise<CashMovement[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      this.supabase
        .from('cash_movements')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to),
    );
    return rows.map(mapMovement);
  }

  /**
   * Mouvements de plusieurs sessions en une passe. Évite le N+1 du tableau de
   * bord (une requête par caisse ouverte) et des statistiques.
   */
  async listMovementsForSessions(sessionIds: string[]): Promise<CashMovement[]> {
    if (sessionIds.length === 0) return [];
    const all: CashMovement[] = [];
    // `in=(…)` passe par l'URL : au-delà de ~200 UUID la requête est rejetée.
    for (const chunk of chunkIds(sessionIds)) {
      const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
        this.supabase
          .from('cash_movements')
          .select('*')
          .in('session_id', chunk)
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to),
      );
      all.push(...rows.map(mapMovement));
    }
    return all;
  }

  async getMovementById(id: string): Promise<CashMovement | null> {
    const { data, error } = await this.supabase
      .from('cash_movements')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapMovement(data) : null;
  }

  async resolveOrCancelAtomicRequest(
    clientRequestId: string,
    requestKind: 'cash_movement' | 'cash_count',
  ): Promise<string | null> {
    const { data, error } = await this.supabase.rpc(
      'resolve_mobile_atomic_request',
      {
        p_client_request_id: clientRequestId,
        p_request_kind: requestKind,
        p_cancel_if_missing: true,
      },
    );
    if (error) throw error;
    return data ?? null;
  }

  async createMovement(input: CreateCashMovementInput): Promise<CashMovement> {
    const { data: sess, error: sessError } = await this.supabase
      .from('cash_sessions')
      .select('is_closed')
      .eq('id', input.sessionId)
      .single();
    if (sessError) throw sessError;
    if ((sess as { is_closed: boolean }).is_closed) throw new Error('caisse.errors.sessionClosed');

    return this.insertMovement({
      clientRequestId: input.clientRequestId,
      sessionId: input.sessionId,
      movementType: input.movementType,
      category: input.category,
      amount: input.amount,
      description: input.description ?? null,
      breakdown: input.breakdown ?? null,
      createdBy: input.createdBy ?? null,
    });
  }

  private async insertMovement(args: {
    clientRequestId?: string;
    sessionId: string;
    movementType: string;
    amount: number;
    category?: string | null;
    description?: string | null;
    /** Détail par coupures — seuls `opening`, `count` et `closing` en portent. */
    breakdown?: CashCountBreakdown | null;
    createdBy?: string | null;
    createdAt?: string;
  }): Promise<CashMovement> {
    const requestId = args.clientRequestId ?? globalThis.crypto.randomUUID();
    const rpcArgs = {
      p_client_request_id: requestId,
      p_session_id: args.sessionId,
      p_movement_type: args.movementType,
      p_category: args.category ?? null,
      p_amount: args.amount,
      p_description: args.description ?? null,
      p_breakdown: readBreakdown(args.breakdown),
      p_created_at: args.createdAt ?? null,
    };
    let { data: movementId, error: createError } = await this.supabase.rpc(
      'create_cash_movement_atomic',
      rpcArgs,
    );
    if (
      (createError && isAmbiguousCashMutationFailure(createError))
      || (!createError && !movementId)
    ) {
      ({ data: movementId, error: createError } = await this.supabase.rpc(
        'create_cash_movement_atomic',
        rpcArgs,
      ));
    }
    if (createError) throw createError;
    if (!movementId) throw new Error('atomic_cash_movement_returned_no_id');
    const { data, error } = await this.supabase
      .from('cash_movements')
      .select('*')
      .eq('id', movementId)
      .single();
    if (error) throw error;
    return mapMovement(data);
  }

  // ------------------------------------------------------------------
  // Réglages
  // ------------------------------------------------------------------

  /**
   * Une ligne manquante ne doit JAMAIS faire échouer la lecture — la caisse
   * doit rester utilisable. On retombe sur les défauts du domaine, qui sont
   * alignés sur les DEFAULT SQL.
   */
  async getSettings(): Promise<CaisseSettings> {
    const { data, error } = await this.supabase
      .from('caisse_settings')
      .select('*')
      .maybeSingle();
    if (error) throw error;
    if (!data) return { ...DEFAULT_CAISSE_SETTINGS };
    return mapSettings(data);
  }

  async updateSettings(input: UpdateCaisseSettingsInput): Promise<CaisseSettings> {
    const tenantId = await this.resolveTenantId();

    const payload: Record<string, unknown> = { tenant_id: tenantId };
    if (input.sessionMode !== undefined) payload['session_mode'] = input.sessionMode;
    if (input.dayCutoffMinutes !== undefined) {
      payload['day_cutoff_minutes'] = input.dayCutoffMinutes;
    }
    if (input.floatCarryMode !== undefined) payload['float_carry_mode'] = input.floatCarryMode;
    if (input.fixedFloat !== undefined) payload['fixed_float'] = input.fixedFloat;
    if (input.countReminder !== undefined) payload['count_reminder'] = input.countReminder;
    if (input.autoAttachCashPayments !== undefined) {
      payload['auto_attach_cash_payments'] = input.autoAttachCashPayments;
    }

    const { data, error } = await this.supabase
      .from('caisse_settings')
      .upsert(payload as never, { onConflict: 'tenant_id' })
      .select()
      .single();
    if (error) throw error;
    return mapSettings(data);
  }

  /**
   * Le tenant DOIT venir du RPC : un admin plateforme voit toutes les lignes
   * via RLS, donc un `from('tenants').limit(1)` renverrait un tenant arbitraire
   * et l'upsert serait rejeté par le WITH CHECK — le réglage reviendrait
   * silencieusement en arrière (cf. settings-repository.ts).
   */
  private async resolveTenantId(): Promise<string> {
    type SbRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: string | null; error: { message: string } | null }>;
    };
    const { data: tenantId, error } = await (this.supabase as unknown as SbRpc).rpc(
      'current_user_tenant_id',
      {},
    );
    if (error) throw new Error(error.message);
    if (!tenantId) throw new Error('not_in_tenant');
    return tenantId;
  }

  // ------------------------------------------------------------------
  // Numérotation
  // ------------------------------------------------------------------

  async getNextSessionNumber(): Promise<string> {
    return this.nextNumber('seq_cash_session', 'CAI');
  }

  async getNextMovementNumber(): Promise<string> {
    return this.nextNumber('seq_cash_movement', 'MVT');
  }

  private async nextNumber(seqName: string, prefix: string): Promise<string> {
    const year = await resolveDocNumberYear(this.supabase);
    const { data, error } = await this.supabase.rpc('get_next_sequence_value', {
      seq_name: seqName,
    });
    if (error && seqName === 'seq_cash_movement') throw error;
    if (!error && data != null) {
      return buildDocumentNumber(prefix, year, Number(data));
    }
    if (seqName === 'seq_cash_movement') {
      throw new Error('atomic_cash_movement_number_required');
    }
    const table = seqName === 'seq_cash_session' ? 'cash_sessions' : 'cash_movements';
    const { count } = await this.supabase
      .from(table)
      .select('*', { count: 'exact', head: true });
    const next = (count ?? 0) + 1;
    return buildDocumentNumber(prefix, year, next);
  }
}

function isAmbiguousCashMutationFailure(error: { code?: string | null }): boolean {
  return typeof error.code !== 'string' || !/^[0-9A-Z]{5}$/.test(error.code);
}

// ====================================================================
// Helpers
// ====================================================================

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function isUniqueViolation(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === '23505';
}

function statsToColumns(stats: CashSessionStats): Record<string, unknown> {
  return {
    stats_total_receipts: stats.totalReceipts,
    stats_cash_receipts: stats.cashReceipts,
    stats_ticket_count: stats.ticketCount,
    stats_sales_amount: stats.salesAmount,
    stats_profit: stats.profit,
    stats_by_method: stats.byMethod,
    // `undefined` deviendrait « champ absent » et laisserait l'ancienne valeur
    // en place sur un recalcul ; on écrit explicitement NULL = « heures
    // inconnues » (aucun règlement horodaté sur cette journée).
    stats_by_hour: stats.byHour ?? null,
    stats_computed_at: stats.computedAt,
  };
}

/**
 * Reconstitue l'instantané, ou `null` s'il n'a jamais été calculé.
 * `null` ≠ « zéro » : l'UI affiche « — » plutôt qu'un faux 0,000 DT.
 */
function mapStats(row: Record<string, unknown>): CashSessionStats | null {
  if (row['stats_computed_at'] == null) return null;
  return {
    totalReceipts: Number(row['stats_total_receipts'] ?? 0),
    cashReceipts: Number(row['stats_cash_receipts'] ?? 0),
    ticketCount: Number(row['stats_ticket_count'] ?? 0),
    salesAmount: Number(row['stats_sales_amount'] ?? 0),
    profit: Number(row['stats_profit'] ?? 0),
    byMethod:
      (row['stats_by_method'] as CashSessionStats['byMethod'] | null) ?? {},
    // Reste `undefined` quand la colonne est NULL — un `{}` se lirait « journée
    // sans la moindre vente », alors que la vérité est « heures inconnues ».
    byHour:
      (row['stats_by_hour'] as CashSessionStats['byHour'] | null) ?? undefined,
    computedAt: row['stats_computed_at'] as string,
  };
}

/**
 * Détail par coupures relu d'une colonne JSONB.
 *
 * Renettoyé À LA LECTURE, et pas seulement à l'écriture : un JSON écrit par une
 * version antérieure, importé, ou bricolé à la main via l'API ne doit jamais
 * faire tomber un écran de caisse. Il perd ses clés inconnues, c'est tout.
 * Un détail vide redevient `null` — « pas de détail », jamais « tiroir vide ».
 */
function readBreakdown(value: unknown): CashCountBreakdown | null {
  if (value == null) return null;
  const clean = sanitizeBreakdown(value);
  return Object.keys(clean).length > 0 ? clean : null;
}

function mapSession(row: Record<string, unknown>): CashSession {
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    bankAccountId: row['bank_account_id'] as string,
    openedBy: row['opened_by'] as string,
    openedAt: row['opened_at'] as string,
    openingBalance: Number(row['opening_balance'] ?? 0),
    openingBreakdown: readBreakdown(row['opening_breakdown']),
    closedBy: (row['closed_by'] as string) ?? null,
    closedAt: (row['closed_at'] as string) ?? null,
    expectedBalance: row['expected_balance'] != null ? Number(row['expected_balance']) : null,
    countedBalance: row['counted_balance'] != null ? Number(row['counted_balance']) : null,
    countedBreakdown: readBreakdown(row['counted_breakdown']),
    difference: row['difference'] != null ? Number(row['difference']) : null,
    isClosed: (row['is_closed'] as boolean) ?? false,
    notes: (row['notes'] as string) ?? null,
    sessionDate: (row['session_date'] as string) ?? '',
    openMode: ((row['open_mode'] as string) ?? 'manual') === 'auto' ? 'auto' : 'manual',
    closeMode:
      row['close_mode'] == null ? null : row['close_mode'] === 'auto' ? 'auto' : 'manual',
    countedAt: (row['counted_at'] as string) ?? null,
    countedBy: (row['counted_by'] as string) ?? null,
    stats: mapStats(row),
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapMovement(row: Record<string, unknown>): CashMovement {
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    sessionId: row['session_id'] as string,
    movementType: row['movement_type'] as CashMovement['movementType'],
    category: (row['category'] as string) ?? null,
    amount: Number(row['amount'] ?? 0),
    description: (row['description'] as string) ?? null,
    breakdown: readBreakdown(row['breakdown']),
    paymentId: (row['payment_id'] as string) ?? null,
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
  };
}

function mapSettings(row: Record<string, unknown>): CaisseSettings {
  return {
    sessionMode: row['session_mode'] === 'manual' ? 'manual' : 'auto',
    dayCutoffMinutes: Number(row['day_cutoff_minutes'] ?? 0),
    floatCarryMode:
      row['float_carry_mode'] === 'fixed'
        ? 'fixed'
        : row['float_carry_mode'] === 'zero'
          ? 'zero'
          : 'carry',
    fixedFloat: Number(row['fixed_float'] ?? 0),
    countReminder: (row['count_reminder'] as boolean) ?? true,
    autoAttachCashPayments: (row['auto_attach_cash_payments'] as boolean) ?? true,
  };
}
